import { Link } from "react-router-dom";
import { formatKabulDisplay } from "./ScheduleFields";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import type { StatusTone } from "../lib/overview-ops";
import {
  auditActionLabel,
  auditActionTone,
  auditOutcomeDisplay,
  relatedAuditLinks,
  safeTargetLabel,
  sanitizeAuditSummary,
  type AuditListRow,
  type AuditSortKey,
} from "../lib/audit-ops";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

type FilterBarProps = {
  actionFilter: string;
  onActionFilterChange: (value: string) => void;
  onApplyActionFilter: () => void;
  query: string;
  onQueryChange: (value: string) => void;
  entityFilter: string;
  onEntityFilterChange: (value: string) => void;
  entityOptions: string[];
  sort: AuditSortKey;
  onSortChange: (value: AuditSortKey) => void;
  organizationLabel: string;
};

export function AuditFilterBar({
  actionFilter,
  onActionFilterChange,
  onApplyActionFilter,
  query,
  onQueryChange,
  entityFilter,
  onEntityFilterChange,
  entityOptions,
  sort,
  onSortChange,
  organizationLabel,
}: FilterBarProps) {
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label="فیلتر سابقه تغییرات"
    >
      <p className="sites-inventory__muted" style={{ width: "100%" }}>
        دامنه:{" "}
        <strong>
          <LtrIsolate mono>{organizationLabel}</LtrIsolate>
        </strong>{" "}
        (سازمان فعلی — ایزولهٔ مستأجر)
      </p>
      <label className="announcements-filter-bar__field">
        <span>فیلتر عمل (API)</span>
        <input
          value={actionFilter}
          onChange={(e) => onActionFilterChange(e.target.value)}
          placeholder="content.published"
          dir="ltr"
          className="ltr-isolate"
          aria-label="فیلتر عمل ممیزی"
        />
      </label>
      <button type="button" className="btn btn--primary btn--sm" onClick={onApplyActionFilter}>
        اعمال فیلتر API
      </button>
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips"
        role="group"
        aria-label="فیلتر نوع موجودیت"
      >
        <button
          type="button"
          className={`btn btn--sm ${entityFilter === "all" ? "btn--primary" : "btn--ghost"}`}
          onClick={() => onEntityFilterChange("all")}
          aria-pressed={entityFilter === "all"}
        >
          همهٔ انواع
        </button>
        {entityOptions.map((t) => (
          <button
            key={t}
            type="button"
            className={`btn btn--sm ${entityFilter === t ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onEntityFilterChange(t)}
            aria-pressed={entityFilter === t}
          >
            <LtrIsolate mono>{t}</LtrIsolate>
          </button>
        ))}
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">جستجوی صفحه</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="عمل، عامل، خلاصه، شناسه…"
          aria-label="جستجو در رویدادهای بارگذاری‌شده"
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>مرتب‌سازی</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as AuditSortKey)}
          aria-label="مرتب‌سازی سابقه تغییرات"
        >
          <option value="time">زمان</option>
          <option value="action">عمل</option>
          <option value="actor">عامل</option>
          <option value="entity">هدف</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

export function AuditInspectSummary({
  item,
  organizationLabel,
}: {
  item: AuditListRow;
  organizationLabel: string;
}) {
  const outcome = auditOutcomeDisplay(item.action);
  const links = relatedAuditLinks();

  return (
    <section className="content-inspect" aria-labelledby="audit-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">سابقه تغییرات</p>
          <h2 id="audit-inspect-title" className="content-inspect__name">
            {auditActionLabel(item.action)}
          </h2>
          <p className="content-inspect__id">
            شناسه <LtrIsolate mono>{item.id}</LtrIsolate>
          </p>
        </div>
        <div className="content-inspect__badges" aria-label="وضعیت رویداد">
          <span className={`state-badge ${TONE_BADGE[outcome.tone]}`}>{outcome.label}</span>
        </div>
      </header>

      {outcome.detail && <p className="content-inspect__eligibility-detail">{outcome.detail}</p>}

      <dl className="content-inspect__facts">
        <div>
          <dt>زمان (کابل)</dt>
          <dd>
            <LtrIsolate mono>{formatKabulDisplay(item.createdAt)}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>کد عمل</dt>
          <dd>
            <LtrIsolate mono>{item.action}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>عامل</dt>
          <dd>
            {item.actor ? (
              <>
                {item.actor.displayName}
                {" · "}
                <LtrIsolate mono>{item.actor.id}</LtrIsolate>
              </>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>دامنه سازمان</dt>
          <dd>
            <LtrIsolate mono>{organizationLabel}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>هدف ایمن</dt>
          <dd>
            <LtrIsolate mono>{safeTargetLabel(item.entityType, item.entityId)}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>خلاصه</dt>
          <dd>{sanitizeAuditSummary(item.summary)}</dd>
        </div>
      </dl>

      <nav className="content-inspect__links" aria-label="مسیرهای مرتبط مدیریت">
        {links.map((link) => (
          <Link key={link.id} to={link.to} className="btn btn--ghost btn--sm">
            {link.label}
          </Link>
        ))}
      </nav>
    </section>
  );
}

export function AuditInventoryList({
  rows,
  selectedId,
  onSelect,
}: {
  rows: AuditListRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول سابقه تغییرات"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">زمان</th>
              <th scope="col">عمل</th>
              <th scope="col">عامل</th>
              <th scope="col">هدف</th>
              <th scope="col">خلاصه</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const tone = auditActionTone(row.action);
              const target = safeTargetLabel(row.entityType, row.entityId);
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                    >
                      <span
                        className="inventory-cell-truncate"
                        title={formatKabulDisplay(row.createdAt)}
                      >
                        <LtrIsolate mono>{formatKabulDisplay(row.createdAt)}</LtrIsolate>
                      </span>
                    </button>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[tone]}`}>
                      {auditActionLabel(row.action)}
                    </span>
                    <div
                      className="sites-inventory__muted inventory-cell-truncate"
                      title={row.action}
                    >
                      <LtrIsolate mono>{row.action}</LtrIsolate>
                    </div>
                  </td>
                  <td>{row.actor?.displayName ?? "—"}</td>
                  <td>
                    <div className="inventory-cell-truncate" title={target}>
                      <LtrIsolate mono>{target}</LtrIsolate>
                    </div>
                  </td>
                  <td>{sanitizeAuditSummary(row.summary)}</td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.id)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های سابقه تغییرات"
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const tone = auditActionTone(row.action);
          return (
            <li
              key={row.id}
              className={`sites-inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">
                  {auditActionLabel(row.action)}
                </span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[tone]}`}>
                    <LtrIsolate mono>{row.action}</LtrIsolate>
                  </span>
                </span>
                <span className="sites-inventory__muted">
                  <LtrIsolate mono>{formatKabulDisplay(row.createdAt)}</LtrIsolate>
                </span>
                <span className="sites-inventory__muted">
                  {row.actor?.displayName ?? "—"}
                  {" · "}
                  <LtrIsolate mono>
                    {safeTargetLabel(row.entityType, row.entityId)}
                  </LtrIsolate>
                </span>
                <span className="sites-inventory__muted">{sanitizeAuditSummary(row.summary)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}
